import { ExclamationCircleFilled } from "@ant-design/icons";
import { defineMessages, FormattedMessage } from "react-intl";

import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { BasicModal } from "@/ui-components/commonComponents/Modal/BasicModal";
import { Button } from "@/ui-components/commonComponents/Button/Button";
import LanguagePicker from "../../LanguagePicker/LanguagePicker";
import { AssessmentCopy, type AssessmentCopyVariants } from "@/modules/candidate/assessmentCopy";
import styles from "./style.module.css";

const messages = defineMessages({
  assessmentModalBody: {
    id: "candidate.languageSelect.assessment.modalBody",
    defaultMessage:
      "This assessment is available in the languages below. Please select the language you wish to take the assessment in.",
  },
  surveyModalBody: {
    id: "candidate.languageSelect.survey.modalBody",
    defaultMessage:
      "This survey is available in the languages below. Please select the language you wish to take the survey in.",
  },
  assessmentLockWarning: {
    id: "candidate.languageSelect.assessment.lockWarning",
    defaultMessage: "Once you start the assessment, you won't be able to change the language.",
  },
  surveyLockWarning: {
    id: "candidate.languageSelect.survey.lockWarning",
    defaultMessage: "Once you start the survey, you won't be able to change the language.",
  },
});

const modalBodyMessages: AssessmentCopyVariants = {
  assessment: messages.assessmentModalBody,
  survey: messages.surveyModalBody,
};

const lockWarningMessages: AssessmentCopyVariants = {
  assessment: messages.assessmentLockWarning,
  survey: messages.surveyLockWarning,
};

interface LanguageSelectModalProps {
  open: boolean;
  availableLocales: LanguagesIsoEnum[];
  value?: LanguagesIsoEnum;
  onChange: (locale: LanguagesIsoEnum) => void;
  onConfirm: () => void;
  confirming?: boolean;
}

// Non-dismissible language chooser shown on the assessment intro. Uses the shared BasicModal with
// isClosable=false, so there is intentionally no close affordance (no ✕, mask click, or escape): the
// candidate must Confirm, which persists the choice.
export default function LanguageSelectModal({
  open,
  availableLocales,
  value,
  onChange,
  onConfirm,
  confirming = false,
}: LanguageSelectModalProps) {
  return (
    <BasicModal
      isModalOpen={open}
      isClosable={false}
      footer={
        <Button onClick={onConfirm} isLoading={confirming} disabled={confirming || !value}>
          <FormattedMessage id="candidate.languageSelect.confirm" defaultMessage="Confirm" />
        </Button>
      }
    >
      <div className={styles.body}>
        <h2 className={styles.title}>
          <FormattedMessage id="candidate.languageSelect.modalTitle" defaultMessage="Change Language" />
        </h2>
        <hr className={styles.divider} />
        <p className={styles.description}>
          <AssessmentCopy variants={modalBodyMessages} />
        </p>

        <LanguagePicker availableLocales={availableLocales} value={value} onChange={onChange} disabled={confirming} />

        <div className={styles.warning}>
          <ExclamationCircleFilled className={styles.warningIcon} aria-hidden="true" />
          <span>
            <AssessmentCopy variants={lockWarningMessages} />
          </span>
        </div>
      </div>
    </BasicModal>
  );
}
